// Staff: copy a new brand's setup (lead posting, offers, ad-account link,
// optimizer config) from a sibling brand under the same parent. Opens with a
// dry-run preview; nothing is written until "Apply".
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import defaultClient from '@src/api/clients/default';
import { useEffectiveUser } from '@src/components/layout/AppLayout/ClientViewSwitcher';

const url = (clientId) => `app1/clients/${clientId}/brand-clone/`;

function ReportList({ title, rows, render }) {
  if (!rows || rows.length === 0) return null;
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 1.5 }}>{title}</Typography>
      <List dense disablePadding>
        {rows.map((row, i) => (
          <ListItem key={i} disableGutters sx={{ py: 0 }}>
            <ListItemText primary={render(row)} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>
    </>
  );
}

export default function CopySiblingSetupButton({ client }) {
  const user = useEffectiveUser();
  const isStaff = user?.is_staff || user?.is_superuser;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [data, setData] = useState(null);
  const [siblingId, setSiblingId] = useState('');
  const [applied, setApplied] = useState(null);
  const [error, setError] = useState('');

  const loadPreview = (sid) => {
    setLoading(true);
    setError('');
    defaultClient
      .request({ url: url(client.id), payload: sid ? { sibling_id: sid } : {} })
      .then(({ data: body }) => {
        setData(body);
        if (!sid && body.default_sibling_id) setSiblingId(String(body.default_sibling_id));
        if (body.error) setError(body.error);
      })
      .catch((err) => setError(err?.response?.data?.detail || 'Could not load the preview.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) {
      setApplied(null);
      loadPreview('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!isStaff || !client?.id || !client?.parent_company_name) return null;

  const handleApply = () => {
    setApplying(true);
    setError('');
    defaultClient
      .request({ url: url(client.id), method: 'POST', payload: { sibling_id: siblingId || null } })
      .then(({ data: body }) => setApplied(body))
      .catch((err) => setError(err?.response?.data?.detail || 'Copy failed.'))
      .finally(() => setApplying(false));
  };

  const report = applied || data?.preview;

  return (
    <>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Copy setup from sibling brand
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy setup from a sibling brand</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copies lead posting, offers, the ad-account link (with this brand's own brand code)
            and optimizer settings. Budgets, territories, ZIPs, pages and landing pages are never
            copied, and nothing this brand already has is overwritten.
          </Typography>
          {data?.siblings?.length ? (
            <FormControl fullWidth size="small" sx={{ mb: 1 }}>
              <InputLabel id="sibling-label">Sibling</InputLabel>
              <Select
                labelId="sibling-label"
                label="Sibling"
                value={siblingId}
                disabled={!!applied}
                onChange={(e) => {
                  setSiblingId(e.target.value);
                  loadPreview(e.target.value);
                }}
              >
                {data.siblings.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name} ({s.status}{s.launch_date ? `, launched ${s.launch_date}` : ''})
                    {s.id === data.default_sibling_id ? ' — suggested' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {loading ? (
            <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={24} /></Stack>
          ) : null}
          {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}
          {applied ? <Alert severity="success" sx={{ mt: 1 }}>Copied from {applied.sibling_name}.</Alert> : null}
          {!loading && report ? (
            <>
              {!applied ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Preview — nothing has been written yet.
                  {report.brand_code ? ` Brand code: ${report.brand_code}.` : ''}
                </Alert>
              ) : null}
              <ReportList
                title={applied ? 'Copied' : 'Will copy'}
                rows={report.copied}
                render={(r) => `${r.part}: ${r.detail || ''}`}
              />
              <ReportList
                title="Skipped"
                rows={report.skipped}
                render={(r) => `${r.part}${r.detail ? ` (${r.detail})` : ''}: ${r.reason}`}
              />
              <ReportList title="Notes" rows={report.notes} render={(n) => n} />
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
          {!applied ? (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={applying || loading || !report || !(report.copied || []).length}
            >
              {applying ? 'Copying…' : 'Apply'}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </>
  );
}
